import React from 'react';
import { Box } from '@cp/ds/src/components/box';
import { Container } from '@cp/ds/src/components/container';
import { Grid } from '@cp/ds/src/components/grid';
import { Schema } from '@cp/ds/src/components/schema';
import { StyledListItem } from '@cp/ds/src/components/styled/styledListItem';
import { StyledUnorderedList } from '@cp/ds/src/components/styled/styledUnorderedList';
import { Typography } from '@cp/ds/src/components/typography';
import { alpha } from '@mui/system';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import loadable from '@loadable/component';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { useClientWebRouteParams } from '@cp/entities/clientWebRoutes';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { RoutedLink as Link } from 'src/shared/ui/link';
import { useAppMenuItems } from 'src/entities/menu';
import { useIsEmbedded } from 'src/shared/mediaQueries';
import VectorFullWidth from 'src/assets/shapes/vectors/vectorFullWidth.svg';
import VectorFullWidthSmall from 'src/assets/shapes/vectors/vectorFullWidthSmall.svg';
import { getOrganzationSchema } from '../../../../schemas/organization';
import { FooterBottom } from './components/bottom';
import { FooterMenuSection } from './components/menuSection';
import { FooterTop } from './components/top';
import { FOOTER_BACKGROUND_OPACITY, WIDTH_QR_CODE_SECTION } from './const';

const QrCodeSection = loadable(() => import('./components/qrCodeSection'));
const MobileAppLinks = loadable(() => import('src/entities/mobileApp/links'));
const SupportSection = loadable(() => import('./components/supportSection'));

export const View: React.FC = () => {
  const { pageId } = useClientWebRouteParams();
  const isMobile = useIsMobile();
  const theme = useTheme();
  const { mainMenuItems } = useAppMenuItems();
  const isEmbedded = useIsEmbedded();

  const pages = [
    ClientWebPage.Catalog,
    ClientWebPage.CatalogItem,
    ClientWebPage.Profile,
    ClientWebPage.ProfileBilling,
    ClientWebPage.ProfileVacancies,
  ];

  const baseColor = pageId && pages.includes(pageId) ? 'background.paper' : 'white';

  if (isEmbedded) {
    return null;
  }

  return (
    <Box>
      <Box
        sx={{
          backgroundColor: { xs: baseColor, md: 'gray light' },
          width: '100%',
          color: { xs: 'gray light', md: baseColor },
          transform: { xs: 'scaleY(-1)', md: 'scaleX(-1)' },
          mb: '-2px',
        }}
      >
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <VectorFullWidth />
        </Box>

        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <VectorFullWidthSmall />
        </Box>
      </Box>
      <Box
        sx={{
          pt: { xs: 1, md: 4 },
          backgroundColor: alpha(theme.palette['gray light'], FOOTER_BACKGROUND_OPACITY),
          color: 'brand gray',
          overflow: 'hidden',
        }}
      >
        <FooterTop />
        <Container sx={{ overflow: 'hidden', pb: { md: 2.5 } }}>
          <Grid columnSpacing={1} container position="relative">
            {mainMenuItems.map((category, index) => (
              <FooterMenuSection
                details={
                  <StyledUnorderedList>
                    {category.items.map(({ link, label, external }, i) => (
                      <StyledListItem key={`${label}-${i}`} sx={{ py: { xs: 1, md: 0.5 } }}>
                        <Link external={external} href={link} sx={{ color: 'gray' }} underline={false}>
                          <Typography variant="body bold">{label}</Typography>
                        </Link>
                      </StyledListItem>
                    ))}
                  </StyledUnorderedList>
                }
                id={index}
                key={index}
                summary={
                  <Typography
                    component="h6"
                    sx={{ typography: { xs: 'button', md: 'button secondary' }, color: 'brand gray', mt: 0, mb: { xs: 0, md: 2 } }}
                  >
                    {category.title}
                  </Typography>
                }
              />
            ))}
            {!isMobile && (
              <>
                <SupportSection />
                <QrCodeSection />
              </>
            )}
          </Grid>
          {isMobile && (
            <Box sx={{ py: 5.75 }}>
              <SupportSection />
              <MobileAppLinks width={{ xs: 1, md: WIDTH_QR_CODE_SECTION }} />
            </Box>
          )}
        </Container>
        <FooterBottom />
        <Schema schema={getOrganzationSchema()} />
      </Box>
    </Box>
  );
};
